<template>
  <div v-if="show" class="fixed inset-0 z-50 overflow-y-auto">
    <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
      <!-- Background overlay -->
      <div class="fixed inset-0 transition-opacity" aria-hidden="true">
        <div class="absolute inset-0 bg-gray-900 opacity-75"></div>
      </div>

      <!-- Modal panel -->
      <div class="inline-block align-bottom bg-gradient-to-br from-slate-800 to-slate-900 rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
        <div class="sm:flex sm:items-start">
          <div class="mt-3 text-center sm:mt-0 sm:text-left w-full">
            <h3 class="text-2xl leading-6 font-semibold text-white mb-6">
              {{ isSubscribed ? 'Manage Subscription' : 'Subscribe to PaperBoi' }}
            </h3>

            <!-- Subscription Status -->
            <div v-if="isSubscribed" class="mb-6 p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
              <p class="text-green-400">
                <i class="pi pi-check-circle mr-2"></i>
                Active Subscription
              </p>
              <p class="text-sm text-gray-300 mt-2">
                Next billing date: {{ formatDate(subscriptionData.current_period_end) }}
              </p>
            </div>

            <!-- Pricing Details -->
            <div v-if="!isSubscribed" class="mb-6">
              <div class="text-center">
                <div class="flex items-baseline justify-center">
                  <span class="text-5xl font-bold text-white">$9.99</span>
                  <span class="text-gray-400 ml-2">/month</span>
                </div>
                <p class="mt-4 text-gray-300">Unlock unlimited access to AI-powered paper analysis</p>
              </div>

              <div class="mt-6 space-y-4">
                <div class="flex items-center text-gray-300">
                  <i class="pi pi-check text-green-400 mr-3"></i>
                  <span>Unlimited paper analysis</span>
                </div>
                <div class="flex items-center text-gray-300">
                  <i class="pi pi-check text-green-400 mr-3"></i>
                  <span>Smart summaries & insights</span>
                </div>
                <div class="flex items-center text-gray-300">
                  <i class="pi pi-check text-green-400 mr-3"></i>
                  <span>Advanced study tools</span>
                </div>
              </div>
            </div>

            <!-- Action Buttons -->
            <div class="mt-6">
              <button
                v-if="!isSubscribed"
                @click="handleSubscribe"
                class="w-full px-4 py-3 bg-gradient-to-r from-purple-500 to-purple-700 text-white rounded-lg font-semibold hover:from-purple-600 hover:to-purple-800 transition-all duration-200"
                :disabled="loading"
              >
                <span v-if="loading" class="flex items-center justify-center">
                  <i class="pi pi-spinner animate-spin mr-2"></i>
                  Processing...
                </span>
                <span v-else>Subscribe Now</span>
              </button>

              <div v-else class="space-y-3">
                <button
                  @click="handleManageSubscription"
                  class="w-full px-4 py-3 bg-white/10 text-white rounded-lg font-semibold hover:bg-white/20 transition-all duration-200"
                >
                  Manage Billing
                </button>
                <button
                  @click="handleCancelSubscription"
                  class="w-full px-4 py-3 bg-red-500/10 text-red-400 rounded-lg font-semibold hover:bg-red-500/20 transition-all duration-200"
                >
                  Cancel Subscription
                </button>
              </div>
            </div>

            <!-- Close Button -->
            <button
              @click="$emit('close')"
              class="mt-4 w-full px-4 py-2 text-gray-400 hover:text-white transition-colors duration-200"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'SubscriptionModal',
  props: {
    show: Boolean,
    isSubscribed: Boolean,
    subscriptionData: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      loading: false
    }
  },
  methods: {
    formatDate(timestamp) {
      if (!timestamp) return '';
      return new Date(timestamp * 1000).toLocaleDateString();
    },
    async handleSubscribe() {
      this.loading = true;
      const pbUser = this.$cookies.get('pb_user');
      const pbToken = this.$cookies.get('pb_token');
      
      try {
        const response = await axios.post(
          `${process.env.VUE_APP_BASE_URL}/create-checkout-session`,
          {},
          {
            headers: {
              'Token': `Bearer ${pbToken}`,
              'User': pbUser
            }
          }
        );
        
        // Redirect to Stripe Checkout
        window.location.href = response.data.url;
      } catch (error) {
        console.error('Error creating checkout session:', error);
        alert('Failed to start subscription process');
      } finally {
        this.loading = false;
      }
    },
    async handleManageSubscription() {
      const pbUser = this.$cookies.get('pb_user');
      const pbToken = this.$cookies.get('pb_token');
      
      try {
        const response = await axios.post(
          `${process.env.VUE_APP_BASE_URL}/create-portal-session`,
          {},
          {
            headers: {
              'Token': `Bearer ${pbToken}`,
              'User': pbUser
            }
          }
        );
        
        // Redirect to Stripe Customer Portal
        window.location.href = response.data.url;
      } catch (error) {
        console.error('Error creating portal session:', error);
        alert('Failed to open billing portal');
      }
    },
    async handleCancelSubscription() {
      if (!confirm('Are you sure you want to cancel your subscription? You\'ll continue to have access until the end of your billing period.')) {
        return;
      }
      
      // Redirect to portal for cancellation
      await this.handleManageSubscription();
    }
  }
}
</script> 